import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useAsync } from "../hooks/useAsync";
import { StateBlock } from "../components/StateBlock";
import { ApiError, ASSET_BUCKET_URL, type ApiClient } from "../api/client";
import type {
  AssetCatalogEntry,
  AssetSourcePlatform,
  AssetStatus,
  CreateAssetInput,
  MetaPage,
} from "../api/types";

const SOURCE_PLATFORMS: AssetSourcePlatform[] = [
  "canva",
  "hubspot",
  "sharepoint",
  "loom",
  "heygen",
  "amazon_ads",
  "s3",
  "other",
];

/** Aspect presets → Stable Diffusion-friendly pixel dimensions. */
const ASPECTS = [
  { label: "Square (1:1)", width: 1024, height: 1024 },
  { label: "Landscape (16:9)", width: 1280, height: 720 },
  { label: "Portrait (9:16)", width: 720, height: 1280 },
];

export function AssetLibrary({ brandId }: { brandId: string }) {
  const { api } = useAuth();
  const { data, loading, error, reload } = useAsync(
    (signal) =>
      api.listAssets(
        brandId.trim() ? { brandId: brandId.trim() } : undefined,
        signal,
      ),
    [brandId],
    true,
  );

  const assets = data?.assets ?? [];
  const [addOpen, setAddOpen] = useState(false);

  return (
    <section aria-labelledby="al-heading">
      <div className="al-header">
        <div>
          <h2 id="al-heading">Asset Library</h2>
          <p className="section-note">
            Generate creative with AI, review it, and publish to a platform.
          </p>
        </div>
        <div className="al-actions">
          <a
            className="asset-link al-bucket-link"
            href={ASSET_BUCKET_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Asset Store bucket ↗
          </a>
          <button type="button" onClick={() => setAddOpen(true)}>
            + Add image / video
          </button>
        </div>
      </div>

      <StateBlock
        loading={loading}
        error={error}
        isEmpty={assets.length === 0}
        emptyMessage='No assets yet. Use "Add image / video" to generate one.'
        onRetry={reload}
      >
        <ul className="asset-list">
          {assets.map((asset) => (
            <AssetCard key={asset.id} asset={asset} onChanged={reload} />
          ))}
        </ul>
      </StateBlock>

      {addOpen && (
        <Modal title="Add image or video" onClose={() => setAddOpen(false)}>
          <AddAssetModal
            brandId={brandId}
            onCreated={() => {
              setAddOpen(false);
              reload();
            }}
          />
        </Modal>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Modal shell — accessible dialog (Escape + backdrop click to close).
// -----------------------------------------------------------------------------

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__head">
          <h3 className="modal__title">{title}</h3>
          <button
            type="button"
            className="modal__close"
            aria-label="Close dialog"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="modal__body">{children}</div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Add-asset modal — "Generate with AI" or "Add by URL".
// -----------------------------------------------------------------------------

function AddAssetModal({
  brandId,
  onCreated,
}: {
  brandId: string;
  onCreated: () => void;
}) {
  const [mode, setMode] = useState<"generate" | "url">("generate");
  return (
    <>
      <div className="modal__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "generate"}
          className={mode === "generate" ? "mtab mtab--active" : "mtab"}
          onClick={() => setMode("generate")}
        >
          ✨ Generate with AI
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "url"}
          className={mode === "url" ? "mtab mtab--active" : "mtab"}
          onClick={() => setMode("url")}
        >
          Add by URL
        </button>
      </div>
      {mode === "generate" ? (
        <GenerateForm brandId={brandId} onCreated={onCreated} />
      ) : (
        <RegisterAssetForm brandId={brandId} onCreated={onCreated} />
      )}
    </>
  );
}

function GenerateForm({
  brandId,
  onCreated,
}: {
  brandId: string;
  onCreated: () => void;
}) {
  const { api } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [title, setTitle] = useState("");
  const [aspect, setAspect] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "err"; text: string } | null>(
    null,
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!prompt.trim()) {
      setMessage({ kind: "err", text: "Prompt is required." });
      return;
    }
    setSubmitting(true);
    setMessage(null);
    const a = ASPECTS[aspect]!;
    try {
      await api.generateAsset({
        prompt: prompt.trim(),
        title: title.trim() || undefined,
        brandId: brandId.trim() || undefined,
        width: a.width,
        height: a.height,
      });
      onCreated();
    } catch (err) {
      setMessage({
        kind: "err",
        text:
          err instanceof ApiError
            ? `Failed (${err.status || "network"}): ${err.message}`
            : String(err),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="asset-form" onSubmit={onSubmit}>
      <label className="asset-field">
        Prompt
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          placeholder="e.g. a vibrant product hero shot of a reusable water bottle on a mountain trail at sunrise"
          required
        />
      </label>
      <div className="asset-form__grid">
        <label>
          Title (optional)
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="defaults to the prompt"
          />
        </label>
        <label>
          Aspect
          <select
            value={aspect}
            onChange={(e) => setAspect(Number(e.target.value))}
          >
            {ASPECTS.map((a, i) => (
              <option key={a.label} value={i}>
                {a.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="asset-form__actions">
        <button type="submit" disabled={submitting}>
          {submitting && <span className="spinner" aria-hidden="true" />}
          {submitting ? "Generating…" : "Generate image"}
        </button>
        {message && (
          <span className={message.kind === "ok" ? "form-ok" : "form-err"} role="alert">
            {message.text}
          </span>
        )}
      </div>
    </form>
  );
}

function RegisterAssetForm({
  brandId,
  onCreated,
}: {
  brandId: string;
  onCreated: () => void;
}) {
  const { api } = useAuth();
  const [title, setTitle] = useState("");
  const [sourcePlatform, setSourcePlatform] =
    useState<AssetSourcePlatform>("other");
  const [contentUrl, setContentUrl] = useState("");
  const [tags, setTags] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "err"; text: string } | null>(
    null,
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setMessage({ kind: "err", text: "Title is required." });
      return;
    }
    if (!contentUrl.trim()) {
      setMessage({ kind: "err", text: "Content URL is required." });
      return;
    }
    setSubmitting(true);
    setMessage(null);
    const input: CreateAssetInput = {
      title: title.trim(),
      sourcePlatform,
      // The UI calls this the "Content URL"; the API field remains externalUrl.
      externalUrl: contentUrl.trim(),
      brandId: brandId.trim() || undefined,
      tags: tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    };
    try {
      await api.createAsset(input);
      onCreated();
    } catch (err) {
      setMessage({
        kind: "err",
        text:
          err instanceof ApiError
            ? `Failed (${err.status || "network"}): ${err.message}`
            : String(err),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="asset-form" onSubmit={onSubmit}>
      <div className="asset-form__grid">
        <label>
          Title
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </label>
        <label>
          Source platform
          <select
            value={sourcePlatform}
            onChange={(e) =>
              setSourcePlatform(e.target.value as AssetSourcePlatform)
            }
          >
            {SOURCE_PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label>
          Content URL
          <input
            type="url"
            value={contentUrl}
            onChange={(e) => setContentUrl(e.target.value)}
            placeholder="https://… (image or video)"
          />
        </label>
        <label>
          Tags (comma-separated)
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="hero, q4, promo"
          />
        </label>
      </div>
      <div className="asset-form__actions">
        <button type="submit" disabled={submitting}>
          {submitting ? "Adding…" : "Add asset"}
        </button>
        {message && (
          <span className={message.kind === "ok" ? "form-ok" : "form-err"} role="alert">
            {message.text}
          </span>
        )}
      </div>
    </form>
  );
}

// -----------------------------------------------------------------------------
// Asset card — preview + status + review/publish actions.
// -----------------------------------------------------------------------------

function AssetCard({
  asset,
  onChanged,
}: {
  asset: AssetCatalogEntry;
  onChanged: () => void;
}) {
  return (
    <li className="asset-card">
      <div className="asset-card__head">
        <h3>{asset.title}</h3>
        <span className="badge">{asset.sourcePlatform}</span>
      </div>

      {asset.s3Key && <AssetPreview assetId={asset.id} />}
      {asset.status && <StatusBadge status={asset.status} />}

      {asset.externalUrl && (
        <a
          href={asset.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="asset-link"
        >
          Open content ↗
        </a>
      )}

      {asset.tags && asset.tags.length > 0 && (
        <ul className="tag-row" aria-label="Tags">
          {asset.tags.map((t) => (
            <li key={t} className="tag">
              {t}
            </li>
          ))}
        </ul>
      )}

      <AssetActions asset={asset} onChanged={onChanged} />
    </li>
  );
}

function StatusBadge({ status }: { status: AssetStatus }) {
  return <span className={`status-badge status-badge--${status}`}>{status}</span>;
}

/** Lazily loads the stored binary through the authenticated proxy as a blob. */
function AssetPreview({ assetId }: { assetId: string }) {
  const { api } = useAuth();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    api
      .assetObjectUrl(assetId)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [assetId, api]);

  if (failed) {
    return <div className="al-preview al-preview--msg">preview unavailable</div>;
  }
  if (!url) {
    return <div className="al-preview al-preview--msg">loading…</div>;
  }
  return <img className="al-preview" src={url} alt="" />;
}

function AssetActions({
  asset,
  onChanged,
}: {
  asset: AssetCatalogEntry;
  onChanged: () => void;
}) {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // Plain URL-registered asset (no review lifecycle): keep the S3 mirror action.
  if (!asset.status) {
    return <MirrorButton assetId={asset.id} mirrored={Boolean(asset.s3Key)} />;
  }

  if (asset.status === "published") {
    return (
      <div className="publish-row">
        <span className="form-ok">
          Published to {asset.publishedTo?.platform ?? "platform"} ✓
        </span>
        {asset.publishedTo?.resourceUrl && (
          <a
            className="asset-link"
            href={asset.publishedTo.resourceUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View post ↗
          </a>
        )}
      </div>
    );
  }

  if (asset.status === "draft") {
    return (
      <div className="publish-row">
        <button
          type="button"
          disabled={busy}
          onClick={() => run(() => api.approveAsset(asset.id))}
        >
          {busy ? "Approving…" : "Approve"}
        </button>
        {err && <span className="form-err">{err}</span>}
      </div>
    );
  }

  // approved → pick a Meta Page + caption, then publish.
  return <PublishControls asset={asset} onChanged={onChanged} />;
}

/** Module-level cache so the Meta page list is fetched once per session. */
let metaPagesPromise: Promise<MetaPage[]> | null = null;
function loadMetaPages(api: ApiClient): Promise<MetaPage[]> {
  if (!metaPagesPromise) {
    metaPagesPromise = api
      .listMetaPages()
      .then((r) => r.pages)
      .catch((e) => {
        metaPagesPromise = null; // allow a retry on next mount
        throw e;
      });
  }
  return metaPagesPromise;
}

function PublishControls({
  asset,
  onChanged,
}: {
  asset: AssetCatalogEntry;
  onChanged: () => void;
}) {
  const { api } = useAuth();
  const [pages, setPages] = useState<MetaPage[] | null>(null);
  const [pagesErr, setPagesErr] = useState<string | null>(null);
  const [pageId, setPageId] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadMetaPages(api)
      .then((ps) => {
        if (cancelled) return;
        setPages(ps);
        if (ps[0]) setPageId(ps[0].id);
      })
      .catch((e) => {
        if (!cancelled) {
          setPagesErr(e instanceof ApiError ? e.message : String(e));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  async function publish() {
    setBusy(true);
    setErr(null);
    try {
      await api.publishAsset(asset.id, caption.trim(), pageId);
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="publish-col">
      {pages && pages.length > 0 && (
        <label className="publish-field">
          Meta Page
          <select value={pageId} onChange={(e) => setPageId(e.target.value)}>
            {pages.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {pagesErr && (
        <span className="form-err">Couldn’t load Meta pages: {pagesErr}</span>
      )}
      <input
        type="text"
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        placeholder="Caption for the Meta post…"
        aria-label="Meta caption"
      />
      <div className="publish-row">
        <button
          type="button"
          disabled={busy || !caption.trim() || (!pageId && !pagesErr)}
          onClick={publish}
        >
          {busy ? "Publishing…" : "Publish to Meta"}
        </button>
        {err && <span className="form-err">{err}</span>}
      </div>
    </div>
  );
}

function MirrorButton({
  assetId,
  mirrored,
}: {
  assetId: string;
  mirrored: boolean;
}) {
  const { api } = useAuth();
  const [state, setState] = useState<"idle" | "working" | "done" | "error">(
    mirrored ? "done" : "idle",
  );
  const [detail, setDetail] = useState<string | null>(null);

  async function onMirror() {
    setState("working");
    setDetail(null);
    try {
      const res = await api.mirrorAsset(assetId);
      setState("done");
      if (res.presignedUrl || res.uploadUrl) {
        setDetail("Presigned URL issued for S3 mirror.");
      } else if (res.s3Key) {
        setDetail(`Mirrored to ${res.s3Key}.`);
      } else {
        setDetail("Mirror requested.");
      }
    } catch (err) {
      setState("error");
      setDetail(err instanceof ApiError ? err.message : "Mirror not available.");
    }
  }

  return (
    <div className="mirror">
      <button
        type="button"
        onClick={onMirror}
        disabled={state === "working"}
        className="mirror-btn"
      >
        {state === "done"
          ? "Mirrored ✓"
          : state === "working"
            ? "Mirroring…"
            : "Mirror to S3"}
      </button>
      {detail && <span className="mirror-detail muted">{detail}</span>}
    </div>
  );
}
